/** @jsx jsx */
import {
  DottedList,
  DottedListItem,
  Flex,
  List,
  ListCellsWrapper,
  ListHeader,
  ListHeaderCell,
  ListItem,
  ListItemCell,
  Paragraph,
} from "@eworkit/iris";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { jsx } from "theme-ui";

import { DateRange } from "@/components/dates";
import { EntityType } from "@/core/types";
import { getJobRequestClientInfo } from "@/data/job-requests";
import { useRelatedJobRequestsQuery } from "@/data/related/queries/useRelatedJobRequestQuery";
import { JobRequestStatusComponent } from "@/views/job-requests/status/JobRequestStatus";
import { JobRequestClientAvatar } from "@/views/JobRequests";
import { router } from "@/views/router";

interface RelatedJobRequestListProps {
  entityType: EntityType;
  entityId: string | number;
  url: string;
}

export const RelatedJobRequestsList: FC<RelatedJobRequestListProps> = ({ entityType, entityId, url }) => {
  const { t } = useTranslation("session");

  const { data } = useRelatedJobRequestsQuery({
    entityType,
    entityId,
    url,
  });

  const columns = {
    title: {
      sx: {
        flexGrow: 1,
        alignSelf: "flex-start",
      },
    },
    period: {
      sx: {
        width: [null, null, null, "200px"],
      },
    },
    status: {
      sx: {
        width: [null, null, null, "130px"],
        justifyContent: ["flex-start", null, "flex-end"],
      },
    },
  };

  return (
    <Flex gap="xs" flexDirection="column">
      <ListHeader>
        <ListCellsWrapper>
          <ListHeaderCell columnConfig={columns.title}>{t("RELATED_ENTITIES.JOB_REQUEST.JOB_TITLE")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.period}>{t("RELATED_ENTITIES.JOB_REQUEST.PERIOD")}</ListHeaderCell>
          <ListHeaderCell columnConfig={columns.status}>{t("RELATED_ENTITIES.JOB_REQUEST.STATUS")}</ListHeaderCell>
        </ListCellsWrapper>
      </ListHeader>

      <List>
        {data.content.map(jobRequest => {
          const clientInfo = getJobRequestClientInfo(jobRequest);

          return (
            <ListItem key={jobRequest.id}>
              <ListCellsWrapper
                as={Link}
                to={{ pathname: router.get(`job-requests.detail`, { jobRequestId: jobRequest.id }).path }}>
                <ListItemCell columnConfig={columns.title}>
                  <Flex gap="xs">
                    <JobRequestClientAvatar jobRequest={jobRequest} />
                    <Flex flexDirection="column">
                      <Paragraph size="p-md" bold ellipsis>
                        {jobRequest.title}
                      </Paragraph>
                      <DottedList>
                        <DottedListItem>
                          <Paragraph size="p-md" color="n700">
                            {jobRequest.systemId}
                          </Paragraph>
                        </DottedListItem>
                        <DottedListItem>
                          <Paragraph size="p-md" color="n700" ellipsis>
                            {clientInfo.name}
                          </Paragraph>
                        </DottedListItem>
                      </DottedList>
                    </Flex>
                  </Flex>
                </ListItemCell>

                {jobRequest.startDate && jobRequest.endDate && (
                  <ListItemCell columnConfig={columns.period}>
                    <DateRange from={jobRequest.startDate} to={jobRequest.endDate} />
                  </ListItemCell>
                )}

                <ListItemCell columnConfig={columns.status}>
                  <JobRequestStatusComponent status={jobRequest.status === "CLOSED" ? "EXPIRED" : jobRequest.status} />
                </ListItemCell>
              </ListCellsWrapper>
            </ListItem>
          );
        })}
      </List>
    </Flex>
  );
};
